'use client';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import type { TDynamicData, TValue } from '../../types/dynamic-data';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useState } from 'react';
import { Paragraph } from '../ui/paragraph';
import { Heading } from '../shared/heading';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function FAQ({ faqs, categories }: { faqs: TDynamicData[]; categories: TValue[] }) {
  const { t } = useTranslations();
  const [activeTab, setActiveTab] = useState<string>(
    categories[0] ? `category-${categories[0].id}` : '',
  );

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 py-12 pr-0 sm:gap-10 sm:py-16 sm:pr-4 lg:gap-12 lg:py-24',
      )}
    >
      <Heading
        as="h1"
        title={t('text581')}
        className={{ title: 'text-center', container: 'pr-4 sm:pr-0' }}
      />
      <Tabs
        onValueChange={value => setActiveTab(value)}
        defaultValue={activeTab}
        value={activeTab}
        className={cn(
          'flex w-full flex-col items-start justify-start gap-6 sm:items-center sm:justify-center sm:gap-8',
        )}
      >
        <TabsList className="hide-scroll-bar flex h-fit w-full flex-nowrap items-start justify-start gap-1 overflow-auto whitespace-nowrap rounded-none bg-transparent p-0 shadow-none sm:gap-2 md:items-center md:justify-center">
          {categories.map(category => (
            <TabsTrigger
              key={category.id}
              value={`category-${category.id}`}
              className={cn(
                'whitespace-nowrap text-sm font-medium sm:text-base',
                'ea-faq__tab-trigger',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        {categories.map(category => (
          <TabsContent
            key={category.id}
            value={`category-${category.id}`}
            className="m-0 mx-auto w-full p-0 pr-4 sm:pr-4"
          >
            <Accordion
              type="single"
              collapsible
              className="mx-auto flex w-full max-w-[900px] flex-col items-center justify-center gap-4"
            >
              {faqs
                .filter(faq => faq.fields[1]?.values?.[0]?.name === category.name)
                .map(faq => (
                  <AccordionItem
                    value={faq.name}
                    key={faq.name}
                    className="w-full rounded-[8px] border border-border p-4"
                  >
                    <AccordionTrigger
                      headingLevel={2}
                      className={cn(
                        'w-full p-0 text-left text-base font-semibold text-foreground sm:text-lg lg:text-xl',
                        'ea-faq__accordion-trigger',
                      )}
                    >
                      {faq.name}
                    </AccordionTrigger>
                    <AccordionContent className="p-0 pt-4">
                      <Paragraph
                        innerHtml={faq.fields[0]?.value || ''}
                        className="w-full"
                        size="lg"
                        color="muted"
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
            </Accordion>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
